import { Alert, Box, Button, Divider, Tab, Tabs, Typography, TextField, Switch, FormControlLabel, Tooltip, Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material';
import StandardDialog from '../dialogs/base/standardDialog';
import React, { useEffect, useState, useRef, useContext } from 'react';
import { toast } from '../../utils/toast';
import { useTranslation } from 'react-i18next';
import { I18nLang, PushNotificationType } from '../../models/user';
import i18n from '../../i18n';
import { apiGet, apiPost, showErrorToast } from '../../services/apiClient';
import { Email, Notifications, Settings, Lock, Tune } from '@mui/icons-material';
import TextWithIcon from '../../utils/TextWithIcon';
import { Body1 } from '../typography';
import { AuthContext } from '../../context/authContext';
import { isPushNotificationsEnabled } from '../../utils/pushNotificationUtils';
import { API_ENDPOINTS, buildApiUrl } from '../../config/apiEndpoints';
import { useTenant } from '../../hooks';
import EmailTab from './EmailTab';

import ScoringTab from './ScoringTab';
import SelectInput from '../inputs/selectInput';
import { PriceSortPolicy, PriceSortPolicyLabels } from '../../models/tenant';

interface ProfileDialogProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onClose: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  if (value !== index) {
    return null;
  }

  return (
    <div
      role="tabpanel"
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      <Box sx={{ p: 3 }}>
        {children}
      </Box>
    </div>
  );
};

interface FormData {
  preferredCountry?: string;
  preferredRadius?: string;
  preferredPositionLat?: number;
  preferredPositionLon?: number;
  workingEmail?: string;
  language?: I18nLang;
  imageUrl?: string;
  workingEmailPassword?: string | null;
  emailNotifications?: boolean;
  smsNotifications?: boolean;
  pushNotifications?: boolean;
  notificationTypes?: Record<string, boolean>;
  notificationSilentTypes?: Record<string, boolean>;
  imapHost?: string;
  imapPort?: string;
  imapUsername?: string;
  imapPassword?: string | null;
  imapSource?: string;
  smtpHost?: string;
  smtpPort?: string;
  smtpSecure?: boolean;
  smtpSource?: string;
  awsSesAccessKeyId?: string;
  awsSesAccessKeySecret?: string | null;
  awsSesLimitPerSecond?: string;
  workingEmailSignaturePlain?: string;
  workingEmailSignatureHtml?: string;
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
  scoringWeights?: Record<string, any>;
  includePlatesOnAutoValidate?: boolean;
  targetPriceMarginFactor?: number;
  potentialOffersRadiusKm?: number;
  minLengthMForFullTruckLoad?: number;
  maxLoadingDaysAhead?: number;
  priceSortPolicy?: PriceSortPolicy;
}

const ProfileDialog = ({ isOpen, setIsOpen, onClose }: ProfileDialogProps) => {
  const { t } = useTranslation();
  const authContext = useContext(AuthContext)!;
  const { tenant } = useTenant();

  // Single form state - same pattern as tenantSettingsModal
  const [form, setForm] = useState<FormData>({});
  const [tabValue, setTabValue] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [togglingPushNotifications, setTogglingPushNotifications] = useState(false);
  const previousPushNotificationsRef = useRef<boolean | undefined>(undefined);

  // Tab configuration
  const tabsConfig = [
    { icon: Settings, label: t('General') },
    { icon: Notifications, label: t('Notifications') },
    { icon: Lock, label: t('Password') },
    { icon: Email, label: t('Email') },
    { icon: Tune, label: t('Scoring') }
  ];

  // Direct change handler - same as tenantSettingsModal
  const handleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const target = e.target as HTMLInputElement;
    const { name, value, type, checked } = target;

    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  const handleNotificationTypeChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const target = e.target as HTMLInputElement;
    const { name, checked } = target;

    setForm(prev => ({
      ...prev,
      notificationTypes: {
        ...prev.notificationTypes,
        [name]: checked,
      }
    }));
  }, []);

  const handleNotificationSilentTypeChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setForm(prev => ({ ...prev, notificationSilentTypes: { ...prev.notificationSilentTypes, [name]: checked } }));
  }, []);

  // Initialize form from user data
  useEffect(() => {
    if (!isOpen || !authContext.user) {
      return;
    }

    const user = authContext.user;
    try {
      let notificationsOptOuts;
      if (user.notificationsOptOuts) {
        try {
          notificationsOptOuts = JSON.parse(user.notificationsOptOuts);
        } catch (parseError) {
          console.error('Failed to parse notificationsOptOuts:', parseError);
          toast.error(t('Failed to load notification preferences. Using default settings.'));
          notificationsOptOuts = {
            email_notifications: true,
            sms_notifications: true,
            push_notifications: true
          };
        }
      } else {
        notificationsOptOuts = {
          email_notifications: true,
          sms_notifications: true,
          push_notifications: true
        };
      }

      const isPushEnabled = isPushNotificationsEnabled(user.platformEndpointArn);

      const notificationTypes = {} as Record<string, boolean>;
      for (const notifType of Object.keys(PushNotificationType)) {
        notificationTypes[notifType] = !user.notificationTypesOptOuts.includes(notifType as PushNotificationType);
      }

      const notificationSilentTypes = {} as Record<string, boolean>;
      for (const notifType of Object.keys(PushNotificationType)) {
        notificationSilentTypes[notifType] = (user.notificationSilentTypes ?? [PushNotificationType.AUTO_SEARCH_COMPLETED])
          .includes(notifType as PushNotificationType);
      }

      const formFromUser: FormData = {
        preferredCountry: user.preferredCountry,
        preferredRadius: user.preferredRadius?.toString(),
        preferredPositionLat: user.preferredPosition?.latitude,
        preferredPositionLon: user.preferredPosition?.longitude,
        workingEmail: user.workingEmail,
        language: user.language ?? I18nLang.EN,
        imageUrl: user.imageUrl,
        workingEmailPassword: user.workingEmailPassword,
        emailNotifications: notificationsOptOuts.email_notifications,
        smsNotifications: notificationsOptOuts.sms_notifications,
        pushNotifications: isPushEnabled,
        notificationTypes: notificationTypes,
        notificationSilentTypes: notificationSilentTypes,
        imapHost: user.imapHost,
        imapPort: user.imapPort?.toString(),
        imapUsername: user.imapUsername,
        imapPassword: user.imapPassword,
        imapSource: user.imapSource?.toString(),
        smtpHost: user.smtpHost,
        smtpPort: user.smtpPort?.toString(),
        smtpSecure: user.smtpSecure,
        smtpSource: user.smtpSource?.toString(),
        awsSesAccessKeyId: user.awsSesAccessKeyId,
        awsSesAccessKeySecret: user.awsSesAccessKeySecret,
        awsSesLimitPerSecond: user.awsSesLimitPerSecond?.toString(),
        workingEmailSignaturePlain: user.workingEmailSignaturePlain,
        workingEmailSignatureHtml: user.workingEmailSignatureHtml,
        scoringWeights: user.tenantOverrides?.scoringWeights || {},
        includePlatesOnAutoValidate: user.tenantOverrides?.includePlatesOnAutoValidate ?? undefined,
        targetPriceMarginFactor: user.tenantOverrides?.targetPriceMarginFactor ?? undefined,
        potentialOffersRadiusKm: user.tenantOverrides?.potentialOffersRadiusKm ?? undefined,
        minLengthMForFullTruckLoad: user.tenantOverrides?.minLengthMForFullTruckLoad ?? undefined,
        maxLoadingDaysAhead: user.tenantOverrides?.maxLoadingDaysAhead ?? undefined,
        priceSortPolicy: user.tenantOverrides?.priceSortPolicy ?? undefined,
      };

      setForm(formFromUser);
      previousPushNotificationsRef.current = isPushEnabled;
    } catch (error) {
      console.error('Error loading user profile data:', error);
      toast.error(t('Failed to load profile data. Please try again.'));
      setForm({});
    }
  }, [isOpen, authContext.user, t]);

  // Watch for language changes
  useEffect(() => {
    if (form.language !== undefined && authContext.user?.language !== form.language) {
      const lang = I18nLang[form.language].toLocaleLowerCase();
      i18n.changeLanguage(lang);
      localStorage.setItem('notea-lang', lang);
    }
  }, [form.language, authContext.user]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Watch for push notification changes
  useEffect(() => {
    if (previousPushNotificationsRef.current === undefined ||
      form.pushNotifications === undefined ||
      previousPushNotificationsRef.current === form.pushNotifications) {
      return;
    }

    const handlePushNotificationChange = async () => {
      setTogglingPushNotifications(true);
      const newValue = form.pushNotifications;

      try {
        let success = false;
        if (newValue) {
          success = await authContext.enablePushNotifications();
          if (success) {
            toast.success(t('Push notifications enabled successfully'));
            previousPushNotificationsRef.current = true;
          } else {
            toast.error(t('Failed to enable push notifications. Please check browser permissions.'));
            setForm(prev => ({ ...prev, pushNotifications: false }));
            previousPushNotificationsRef.current = false;
          }
        } else {
          success = await authContext.disablePushNotifications();
          if (success) {
            toast.success(t('Push notifications disabled'));
            previousPushNotificationsRef.current = false;
          } else {
            toast.error(t('Failed to disable push notifications'));
            setForm(prev => ({ ...prev, pushNotifications: true }));
            previousPushNotificationsRef.current = true;
          }
        }
      } catch (error) {
        console.error('Error toggling push notifications:', error);
        toast.error(t('An error occurred while changing notification settings'));
        setForm(prev => ({ ...prev, pushNotifications: previousPushNotificationsRef.current }));
      } finally {
        setTogglingPushNotifications(false);
      }
    };

    handlePushNotificationChange();
  }, [form.pushNotifications, authContext, t]);

  const linkMail = async (mail: string) => {
    const response = await apiGet(process.env.REACT_APP_API_URL + `/api/v1/users/${mail}/auth_url`);
    const data = await response.json();
    if (data.result) {
      localStorage.setItem('pendingUserMailProvider', mail);
      window.open(data.result, '_self');
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);

      // If password fields are filled, validate and change password
      if (form.currentPassword || form.newPassword || form.confirmPassword) {
        if (!form.currentPassword) {
          toast.error(t('Please enter your current password'));
          return;
        }
        if (!form.newPassword) {
          toast.error(t('Please enter a new password'));
          return;
        }
        if (form.newPassword !== form.confirmPassword) {
          toast.error(t('New passwords do not match'));
          return;
        }
        if (form.newPassword.length < 8) {
          toast.error(t('Password must be at least 8 characters long'));
          return;
        }

        try {
          await apiPost(buildApiUrl(API_ENDPOINTS.users.changePassword), {
            current_password: form.currentPassword,
            password: form.newPassword
          });
          toast.success(t('Password changed successfully!'));
        } catch (error) {
          showErrorToast(error, t('Failed to change password!'));
          return;
        }
      }

      const payload = {
        preferred_country: form.preferredCountry,
        preferred_radius: form.preferredRadius ? Number(form.preferredRadius) : undefined,
        preferred_position: {
          latitude: isNaN(Number(form.preferredPositionLat)) ? undefined : Number(form.preferredPositionLat),
          longitude: isNaN(Number(form.preferredPositionLon)) ? undefined : Number(form.preferredPositionLon),
        },
        working_email: form.workingEmail,
        language: form.language,
        image_url: form.imageUrl,
        working_email_password: form.workingEmailPassword,
        notifications_opt_outs: {
          email_notifications: form.emailNotifications,
          sms_notifications: form.smsNotifications,
          push_notifications: form.pushNotifications,
        },
        notification_types_opt_outs: form.notificationTypes
          ? Array.from(Object.entries(form.notificationTypes)).filter(([_, checked]) => !checked).map(([key, _]) => key)
          : [],
        notification_silent_types: form.notificationSilentTypes
          ? Object.entries(form.notificationSilentTypes).filter(([_, isSilent]) => isSilent).map(([key]) => key)
          : [PushNotificationType.AUTO_SEARCH_COMPLETED],
        imap_host: form.imapHost,
        imap_port: form.imapPort ? Number(form.imapPort) : undefined,
        imap_username: form.imapUsername,
        imap_password: form.imapPassword,
        imap_source: form.imapSource ? Number(form.imapSource) : undefined,
        smtp_host: form.smtpHost,
        smtp_port: form.smtpPort ? Number(form.smtpPort) : undefined,
        smtp_secure: form.smtpSecure,
        smtp_source: form.smtpSource ? Number(form.smtpSource) : undefined,
        aws_ses_access_key_id: form.awsSesAccessKeyId,
        aws_ses_access_key_secret: form.awsSesAccessKeySecret,
        aws_ses_limit_per_second: form.awsSesLimitPerSecond ? Number(form.awsSesLimitPerSecond) : undefined,
        working_email_signature_plain: form.workingEmailSignaturePlain,
        working_email_signature_html: form.workingEmailSignatureHtml,
        tenant_overrides: {
          scoringWeights: form.scoringWeights,
          ...(form.includePlatesOnAutoValidate !== undefined ? { includePlatesOnAutoValidate: form.includePlatesOnAutoValidate } : {}),
          ...(form.targetPriceMarginFactor !== undefined ? { targetPriceMarginFactor: form.targetPriceMarginFactor } : {}),
          ...(form.potentialOffersRadiusKm !== undefined ? { potentialOffersRadiusKm: form.potentialOffersRadiusKm } : {}),
          ...(form.minLengthMForFullTruckLoad !== undefined ? { minLengthMForFullTruckLoad: form.minLengthMForFullTruckLoad } : {}),
          ...(form.maxLoadingDaysAhead !== undefined ? { maxLoadingDaysAhead: form.maxLoadingDaysAhead } : {}),
          ...(form.priceSortPolicy !== undefined ? { priceSortPolicy: form.priceSortPolicy } : {}),
        },
      };

      await apiPost(buildApiUrl(API_ENDPOINTS.users.preferences), payload);
      authContext.setUserFetchCounter((prev: number) => prev + 1);
      toast.success(t('Profile saved successfully!'));
      setForm({});
      setIsOpen(false);
    } catch (error) {
      showErrorToast(error, t('Failed to save profile!'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleScoringWeightsChange = React.useCallback((newWeights: Record<string, any>) => {
    setForm(prev => ({
      ...prev,
      scoringWeights: newWeights
    }));
  }, []);

  const languageOptions = [
    { key: 'English', value: I18nLang.EN },
    { key: 'Français', value: I18nLang.FR },
    { key: 'Deutsch', value: I18nLang.DE },
    { key: 'Español', value: I18nLang.ES },
  ];

  const switchStyle = {
    '& .MuiSwitch-switchBase.Mui-checked': {
      color: 'background.black !important',
      '& + .MuiSwitch-track': {
        backgroundColor: 'background.black !important',
      },
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: 'background.black !important',
    },
    '& .MuiSwitch-switchBase': {
      color: 'text.light',
    },
    '& .MuiSwitch-track': {
      backgroundColor: 'background.darkGrey',
    },
  };
  return (
    <StandardDialog
      open={isOpen}
      onClose={onClose}
      title={<TextWithIcon label={t('Profile Settings')} Icon={Settings} />}
      subtitle={t('Manage your profile settings')}
      maxWidth="lg"
      fullHeight={true}
      height={{ xs: '95vh', sm: '90vh', md: '85vh', lg: '80vh' }}
      primaryAction={{
        label: submitting ? t('Saving...') : t('Save'),
        onClick: handleSubmit,
        loading: submitting,
        disabled: submitting,
      }}
      secondaryAction={{
        label: t('Cancel'),
        onClick: onClose,
      }}
      contentSx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              color: 'text.grey',
              '&.Mui-selected': {
                color: 'text.darkgrey',
                fontWeight: 600,
              },
              '&:hover': {
                color: 'text.darkGrey',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: 'background.shadowBlack',
            },
          }}
        >
          {tabsConfig.map((tab, index) => (
            <Tab
              key={index}
              id={`profile-tab-${index}`}
              aria-controls={`profile-tabpanel-${index}`}
              label={<TextWithIcon label={tab.label} Icon={tab.icon} />}
            />
          ))}
        </Tabs>
      </Box>

      {/* General Tab */}
      <TabPanel value={tabValue} index={0}>
        {authContext.user && form.language !== undefined && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <Body1 mb={2}>{t('Update your general profile information below.')}</Body1>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                <TextField
                  fullWidth
                  label={t('Working Email')}
                  name="workingEmail"
                  value={form.workingEmail || ''}
                  onChange={handleChange}
                />
                <SelectInput
                  fullWidth
                  label={t('Language')}
                  name="language"
                  value={String(form.language)}
                  onChange={handleChange}
                  options={languageOptions.map(opt => ({ key: opt.key, value: String(opt.value) }))}
                />
              </Box>
              <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' }, mt: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.includePlatesOnAutoValidate ?? false}
                      onChange={(e) => setForm(prev => ({ ...prev, includePlatesOnAutoValidate: e.target.checked }))}
                    />
                  }
                  label={t('Include Plate Numbers on Auto Validation')}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -1 }}>
                  {t('Append truck and trailer plate numbers to acceptance emails when auto-validating (overrides company setting)')}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  {t('Price & Search Preferences')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Tooltip title={t('Restore global company settings if available')} enterDelay={500}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setForm(prev => ({
                          ...prev,
                          targetPriceMarginFactor: tenant?.targetPriceMarginFactor,
                          potentialOffersRadiusKm: tenant?.potentialOffersRadiusKm,
                          minLengthMForFullTruckLoad: tenant?.minLengthMForFullTruckLoad,
                          maxLoadingDaysAhead: tenant?.maxLoadingDaysAhead,
                          priceSortPolicy: tenant?.priceSortPolicy
                        }));
                      }}
                    >
                      {t('Reset to Defaults')}
                    </Button>
                  </Tooltip>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('Customize how offers are priced and searched. Leave blank to use your company defaults.')}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                <TextField
                  fullWidth
                  label={t('Target Price Margin Factor')}
                  type="number"
                  inputProps={{ step: '0.01', min: 1 }}
                  value={form.targetPriceMarginFactor ?? ''}
                  onChange={(e) => setForm(prev => ({ ...prev, targetPriceMarginFactor: e.target.value === '' ? undefined : Number(e.target.value) }))}
                  helperText={t('Multiplier applied to target price (e.g., 1.0 = no margin, 1.2 = +20%)')}
                />
                <TextField
                  fullWidth
                  label={t('Potential Offers Radius (km)')}
                  type="number"
                  inputProps={{ step: '1', min: 0 }}
                  value={form.potentialOffersRadiusKm ?? ''}
                  onChange={(e) => setForm(prev => ({ ...prev, potentialOffersRadiusKm: e.target.value === '' ? undefined : Number(e.target.value) }))}
                  helperText={t('Search radius in kilometers for potential offers')}
                />
                <TextField
                  fullWidth
                  label={t('Minimum Length (m) for FTL')}
                  type="number"
                  inputProps={{ step: '1', min: 0 }}
                  value={form.minLengthMForFullTruckLoad ?? ''}
                  onChange={(e) => setForm(prev => ({ ...prev, minLengthMForFullTruckLoad: e.target.value === '' ? undefined : Number(e.target.value) }))}
                  helperText={t('Minimum load length in meters to qualify as a full truck load')}
                />
                <TextField
                  fullWidth
                  label={t('Max Loading Days Ahead')}
                  type="number"
                  inputProps={{ step: '1', min: 1 }}
                  value={form.maxLoadingDaysAhead ?? ''}
                  onChange={(e) => setForm(prev => ({ ...prev, maxLoadingDaysAhead: e.target.value === '' ? undefined : Number(e.target.value) }))}
                  helperText={t('Maximum number of days ahead for auto-search loading date range')}
                />
                <SelectInput
                  fullWidth
                  label={t('Price Sort Policy')}
                  value={form.priceSortPolicy !== undefined ? String(form.priceSortPolicy) : ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setForm(prev => ({ ...prev, priceSortPolicy: val === '' ? undefined : Number(val) as PriceSortPolicy }));
                  }}
                  options={[
                    { key: t('Use company default'), value: '' },
                    ...Object.entries(PriceSortPolicyLabels).map(([value, label]) => ({ key: label, value }))
                  ]}
                  helperText={t('How offers are sorted by price')}
                />
              </Box>
            </Box>
          </Box>
        )}
      </TabPanel>

      {/* Notifications Tab */}
      <TabPanel value={tabValue} index={1}>
        {authContext.user && form.language !== undefined && (
          <Box>
            <Body1 mb={2}>{t('Manage your notification preferences below.')}</Body1>
            {!form.pushNotifications && 'Notification' in window && Notification.permission === 'denied' && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ fontSize: (theme: any) => theme.typography.fontSizes.small }}>
                  {t('Browser notifications are blocked. Please enable them in your browser settings.')}
                </Typography>
              </Alert>
            )}
            {form.pushNotifications && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ fontSize: (theme: any) => theme.typography.fontSizes.small }}>
                  {t('Push notifications are active.')}
                </Typography>
              </Alert>
            )}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    name="pushNotifications"
                    checked={form.pushNotifications || false}
                    onChange={handleChange}
                    disabled={togglingPushNotifications}
                    sx={switchStyle}
                  />
                }
                label={t('Browser Push Notifications')}
              />
              <FormControlLabel
                control={<Switch disabled />}
                label={t('Email Notifications (Coming Soon)')}
              />
              <FormControlLabel
                control={<Switch disabled />}
                label={t('SMS Notifications (Coming Soon)')}
              />
            </Box>
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                {t('Notification types')}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('Notification type')}</TableCell>
                    <TableCell align="center">{t('Enabled')}</TableCell>
                    <TableCell align="center">{t('Desktop popup')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {form.notificationTypes && Object.keys(form.notificationTypes).sort().map(notifType => {
                    const isEnabled = form.notificationTypes![notifType];
                    const isSilent = form.notificationSilentTypes?.[notifType] ?? false;
                    return (
                      <TableRow key={notifType}>
                        <TableCell>{t(notifType.replaceAll('_', ' '))}</TableCell>
                        <TableCell align="center">
                          <Switch name={notifType} checked={isEnabled} onChange={handleNotificationTypeChange} sx={switchStyle} />
                        </TableCell>
                        <TableCell align="center">
                          <Switch
                            name={notifType}
                            checked={isEnabled && !isSilent}
                            disabled={!isEnabled}
                            onChange={(e) => handleNotificationSilentTypeChange({ ...e, target: { ...e.target, checked: !e.target.checked } } as React.ChangeEvent<HTMLInputElement>)}
                            sx={switchStyle}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Box>
        )}
      </TabPanel>

      {/* Password Tab */}
      <TabPanel value={tabValue} index={2}>
        {authContext.user && form.language !== undefined && (
          <Box>
            <Body1 mb={2}>{t('Change your account password. Use at least 8 characters.')}</Body1>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 3 }}>
              <TextField
                fullWidth
                type="password"
                label={t('Current Password')}
                name="currentPassword"
                value={form.currentPassword || ''}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                type="password"
                label={t('New Password')}
                name="newPassword"
                value={form.newPassword || ''}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                type="password"
                label={t('Confirm New Password')}
                name="confirmPassword"
                value={form.confirmPassword || ''}
                onChange={handleChange}
              />
            </Box>
          </Box>
        )}
      </TabPanel>

      {/* Email Tab */}
      <TabPanel value={tabValue} index={3}>
        {authContext.user && form.language !== undefined && (
          <EmailTab
            type="user"
            form={form}
            onChange={handleChange}
            onChangeEmailSignaturePlain={(e) => setForm(prev => ({ ...prev, workingEmailSignaturePlain: e.target.value }))}
            onChangeEmailSignatureHtml={(e) => setForm(prev => ({ ...prev, workingEmailSignatureHtml: e.target.value }))}
            onLinkEmail={linkMail}
            smtpSource={authContext.user?.smtpSource}
            imapSource={authContext.user?.imapSource}
            emailAddress={authContext.user?.workingEmail}
            variant="textfield"
          />
        )}
      </TabPanel>

      {/* Scoring Tab */}
      <TabPanel value={tabValue} index={4}>
        {authContext.user && form.scoringWeights !== undefined && (
          <ScoringTab
            scoringWeights={form.scoringWeights}
            onWeightsChange={handleScoringWeightsChange}
          />
        )}
      </TabPanel>
    </StandardDialog>
  );
};

export default ProfileDialog;
